import { useEffect, useMemo, useState } from 'react';
import {
  Save, Voicemail, PhoneForwarded, Plus, Trash2, Check, Clock
} from 'lucide-react';
import { routingAPI } from '../services/api.js';

// Voicemail + forwarding contacts directory + business hours.
// Mirrors what the API serves at /routing — see routingService.js for shape.

const DAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
  ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Sao_Paulo',
  'UTC'
];

const DEFAULTS = {
  voicemail: {
    enabled: false,
    greeting: "You've reached our voicemail. Please leave your name, number, and a brief message after the beep.",
    emailTo: '',
    maxLengthSec: 120
  },
  forwarding: {
    enabled: false,
    contacts: [],
    sayBeforeDialing: 'One moment, connecting you now.',
    hours: {
      timezone: 'America/Chicago',
      days: {
        mon: { enabled: false, start: '09:00', end: '17:00' },
        tue: { enabled: false, start: '09:00', end: '17:00' },
        wed: { enabled: false, start: '09:00', end: '17:00' },
        thu: { enabled: false, start: '09:00', end: '17:00' },
        fri: { enabled: false, start: '09:00', end: '17:00' },
        sat: { enabled: false, start: '10:00', end: '14:00' },
        sun: { enabled: false, start: '10:00', end: '14:00' }
      }
    }
  }
};

const blankContact = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  phoneE164: '',
  description: '',
  active: false
});

export default function RoutingEditor() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await routingAPI.get();
        const merged = mergeWithDefaults(r?.data || {});
        if (!cancelled) { setCfg(merged); setOriginal(merged); }
      } catch (err) {
        if (!cancelled && err.status !== 404) setError(err.message || 'Failed to load routing.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(original), [cfg, original]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    try {
      // Strip placeholder ids so server assigns real uuids; trim whitespace.
      const contacts = (cfg.forwarding.contacts || [])
        .map((c) => ({
          ...(c.id && !c.id.startsWith('tmp-') ? { id: c.id } : {}),
          name: (c.name || '').trim(),
          phoneE164: (c.phoneE164 || '').trim(),
          description: (c.description || '').trim() || null,
          active: !!c.active
        }))
        .filter((c) => c.name && c.phoneE164);

      const payload = {
        voicemail: {
          enabled: cfg.voicemail.enabled,
          greeting: cfg.voicemail.greeting,
          emailTo: cfg.voicemail.emailTo || null,
          maxLengthSec: Number(cfg.voicemail.maxLengthSec) || 120
        },
        forwarding: {
          enabled: cfg.forwarding.enabled,
          contacts,
          sayBeforeDialing: cfg.forwarding.sayBeforeDialing,
          hours: cfg.forwarding.hours
        }
      };
      const r = await routingAPI.update(payload);
      const next = mergeWithDefaults(r?.data || {});
      setOriginal(next);
      setCfg(next);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const setVoicemail = (patch) => setCfg((c) => ({ ...c, voicemail: { ...c.voicemail, ...patch } }));
  const setForwarding = (patch) => setCfg((c) => ({ ...c, forwarding: { ...c.forwarding, ...patch } }));
  const setContacts = (next) => setForwarding({ contacts: next });
  const updateContact = (idx, patch) =>
    setContacts(cfg.forwarding.contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const setActive = (idx) =>
    setContacts(cfg.forwarding.contacts.map((c, i) => ({ ...c, active: i === idx })));
  const addContact = () => setContacts([...(cfg.forwarding.contacts || []), blankContact()]);
  const removeContact = (idx) =>
    setContacts(cfg.forwarding.contacts.filter((_, i) => i !== idx));
  const setDay = (key, patch) =>
    setForwarding({
      hours: { ...cfg.forwarding.hours, days: { ...cfg.forwarding.hours.days, [key]: { ...cfg.forwarding.hours.days[key], ...patch } } }
    });

  return (
    <section className="card">
      <h2 className="text-xl text-stone-100 mb-1">Call routing</h2>
      <p className="text-sm text-stone-400 mb-6">
        How Alfred handles calls beyond the AI conversation — voicemail when
        callers want to leave a message, forwarding when they ask for a person.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      <form onSubmit={onSave} className="space-y-7">
        {/* ─── Voicemail ──────────────────────────────────────────────── */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Voicemail size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Voicemail</h3>
            </div>
            <Toggle
              checked={!!cfg.voicemail.enabled}
              onChange={(v) => setVoicemail({ enabled: v })}
              disabled={loading || saving}
            />
          </header>

          <div className="space-y-4">
            <div>
              <label className="field-label" htmlFor="vmGreeting">Greeting</label>
              <textarea
                id="vmGreeting"
                className="field-input min-h-[80px]"
                value={cfg.voicemail.greeting || ''}
                onChange={(e) => setVoicemail({ greeting: e.target.value })}
                disabled={loading || saving || !cfg.voicemail.enabled}
                maxLength={2000}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label" htmlFor="vmEmail">Email voicemails to</label>
                <input
                  id="vmEmail" type="email" className="field-input"
                  placeholder="ops@yourcompany.com"
                  value={cfg.voicemail.emailTo || ''}
                  onChange={(e) => setVoicemail({ emailTo: e.target.value })}
                  disabled={loading || saving || !cfg.voicemail.enabled}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="vmMax">Max length (seconds)</label>
                <input
                  id="vmMax" type="number" min="15" max="600" step="15" className="field-input"
                  value={cfg.voicemail.maxLengthSec}
                  onChange={(e) => setVoicemail({ maxLengthSec: Number(e.target.value) })}
                  disabled={loading || saving || !cfg.voicemail.enabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Forwarding contacts ────────────────────────────────────── */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PhoneForwarded size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Forwarding contacts</h3>
            </div>
            <Toggle
              checked={!!cfg.forwarding.enabled}
              onChange={(v) => setForwarding({ enabled: v })}
              disabled={loading || saving}
            />
          </header>

          <p className="text-xs text-stone-500 mb-3">
            People Alfred can transfer to. Mark one <em>active</em> and that's
            who calls forward to. Forwarding only fires during the business
            hours below.
          </p>

          <div className="space-y-2 mb-3">
            {(cfg.forwarding.contacts || []).length === 0 && (
              <p className="text-sm text-stone-500 italic">No contacts yet.</p>
            )}
            {(cfg.forwarding.contacts || []).map((c, i) => (
              <div key={c.id || i} className={`rounded-lg border p-3 ${c.active ? 'border-alfred-gold/60 bg-alfred-gold/5' : 'border-stone-800'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    disabled={!cfg.forwarding.enabled}
                    className={`sm:col-span-1 inline-flex h-9 items-center justify-center rounded-md border text-xs ${c.active ? 'border-alfred-gold bg-alfred-gold text-stone-950' : 'border-stone-700 text-stone-400 hover:text-alfred-gold'}`}
                    title={c.active ? 'Active — calls forward here' : 'Make this contact active'}
                  >
                    {c.active ? <Check size={14} /> : 'set'}
                  </button>
                  <input
                    className="field-input sm:col-span-3"
                    placeholder="Name"
                    value={c.name}
                    onChange={(e) => updateContact(i, { name: e.target.value })}
                    disabled={loading || saving || !cfg.forwarding.enabled}
                  />
                  <input
                    className="field-input sm:col-span-3"
                    placeholder="Role (Sales, Manager…)"
                    value={c.description || ''}
                    onChange={(e) => updateContact(i, { description: e.target.value })}
                    disabled={loading || saving || !cfg.forwarding.enabled}
                  />
                  <input
                    className="field-input sm:col-span-4"
                    placeholder="+13462580006"
                    value={c.phoneE164 || ''}
                    onChange={(e) => updateContact(i, { phoneE164: e.target.value })}
                    disabled={loading || saving || !cfg.forwarding.enabled}
                  />
                  <button
                    type="button"
                    onClick={() => removeContact(i)}
                    className="sm:col-span-1 text-red-300 hover:text-red-200 inline-flex justify-center"
                    aria-label="Remove contact"
                    disabled={loading || saving}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn-ghost"
            onClick={addContact}
            disabled={loading || saving || !cfg.forwarding.enabled}
          >
            <Plus size={14} /> Add contact
          </button>

          <div className="mt-5">
            <label className="field-label" htmlFor="fwSay">Hand-off line (what Alfred says before dialing)</label>
            <input
              id="fwSay" type="text" className="field-input"
              value={cfg.forwarding.sayBeforeDialing || ''}
              onChange={(e) => setForwarding({ sayBeforeDialing: e.target.value })}
              disabled={loading || saving || !cfg.forwarding.enabled}
            />
          </div>
        </div>

        {/* ─── Business hours ─────────────────────────────────────────── */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Business hours</h3>
            </div>
            <select
              className="field-input max-w-[220px]"
              value={cfg.forwarding.hours.timezone}
              onChange={(e) => setForwarding({ hours: { ...cfg.forwarding.hours, timezone: e.target.value } })}
              disabled={loading || saving || !cfg.forwarding.enabled}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </header>

          <p className="text-xs text-stone-500 mb-3">
            Forwarding only fires during these windows. If no day is enabled,
            forwarding works 24/7. Outside the window, Alfred offers voicemail
            or takes a message instead.
          </p>

          <div className="space-y-1.5">
            {DAYS.map(([key, label]) => {
              const d = cfg.forwarding.hours.days[key];
              return (
                <div key={key} className="grid grid-cols-12 items-center gap-2">
                  <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!d.enabled}
                      onChange={(e) => setDay(key, { enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold"
                      disabled={loading || saving || !cfg.forwarding.enabled}
                    />
                    <span className="text-stone-300 w-10">{label}</span>
                  </label>
                  <input
                    type="time"
                    className="field-input col-span-3"
                    value={d.start}
                    onChange={(e) => setDay(key, { start: e.target.value })}
                    disabled={loading || saving || !cfg.forwarding.enabled || !d.enabled}
                  />
                  <span className="col-span-1 text-center text-stone-500 text-sm">to</span>
                  <input
                    type="time"
                    className="field-input col-span-3"
                    value={d.end}
                    onChange={(e) => setDay(key, { end: e.target.value })}
                    disabled={loading || saving || !cfg.forwarding.enabled || !d.enabled}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={!dirty || saving || loading}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save routing'}
          </button>
          {savedAt && !dirty && (
            <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>
          )}
        </div>
      </form>
    </section>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
        disabled={disabled}
      />
      <span className="text-stone-300">{checked ? 'On' : 'Off'}</span>
    </label>
  );
}

function mergeWithDefaults(data) {
  return {
    voicemail: { ...DEFAULTS.voicemail, ...(data.voicemail || {}) },
    forwarding: {
      ...DEFAULTS.forwarding,
      ...(data.forwarding || {}),
      contacts: Array.isArray(data.forwarding?.contacts) ? data.forwarding.contacts : [],
      hours: {
        ...DEFAULTS.forwarding.hours,
        ...(data.forwarding?.hours || {}),
        days: { ...DEFAULTS.forwarding.hours.days, ...(data.forwarding?.hours?.days || {}) }
      }
    }
  };
}
